import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { FinalLeaderboardEntry } from "@binogi-livequiz/shared";
import { CharacterImage } from "../components/CharacterImage";
import { Leaderboard } from "../components/Leaderboard";
import { ScreenHeader } from "../components/layout/ScreenHeader";
import { Button } from "../components/ui/Button";
import { loadStudentSession } from "../lib/session-storage";

interface RouteState {
  displayName: string;
  finalLeaderboard: FinalLeaderboardEntry[];
  availableLanguages?: string[];
}

export default function PlayFinished() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const state = location.state as RouteState | null;

  // The route state's displayName is forwarded from useStudentRejoin on
  // the previous screen, so it already reflects any server-side rename.
  // Reading localStorage here would be wrong: the key is shared across
  // sessions, so joining another quiz in a second tab would repoint it.
  const myName = state?.displayName ?? "";

  const mine = useMemo(
    () =>
      state?.finalLeaderboard.find((e) => e.displayName === myName) ?? null,
    [state, myName],
  );

  if (!state) {
    return (
      <main className="min-h-full flex items-center justify-center px-6 text-center">
        <p className="text-white/70">{t("play.finished.none")}</p>
      </main>
    );
  }

  const langPickerCodes =
    state.availableLanguages ?? loadStudentSession()?.availableLanguages;

  return (
    <main className="min-h-full px-4 md:px-6 py-6 md:py-10 max-w-md mx-auto">
      <ScreenHeader short wordmark="sm" available={langPickerCodes} />

      <h1 className="text-3xl md:text-4xl font-extrabold text-center mb-6">
        {t("play.finished.heading")}
      </h1>

      {/* Who am I? — front and center. By the end of a quiz many students
          have forgotten which character they were assigned (teacher
          feedback 2026-08-27: scanned this page during the group results
          screenshare, missed the small dim name at the bottom of the rank
          card, and never found their own row in the ranking). */}
      {myName && (
        <section className="flex items-center justify-center gap-4 mb-8 animate-pop">
          <CharacterImage name={myName} size={56} />
          <div className="text-left">
            <div className="text-sm text-white/60">
              {t("play.waiting.you_are")}
            </div>
            <div className="text-3xl font-extrabold leading-tight">
              {myName}
            </div>
          </div>
        </section>
      )}

      {mine && (
        <section className="bg-brand-ink2 rounded-2xl p-6 text-center mb-8 animate-pop">
          <div className="text-sm text-white/60">
            {t("play.finished.your_final_rank")}
          </div>
          <div className="text-6xl font-extrabold tabular-nums mt-1 mb-2">
            #{mine.rank}
          </div>
          <div className="text-lg font-semibold">
            {t("common.points_count", { count: mine.score })}
          </div>
        </section>
      )}

      <h2 className="font-semibold mb-3">
        {t("play.finished.top_n", { count: 10 })}
      </h2>
      <Leaderboard
        entries={state.finalLeaderboard.slice(0, 10)}
        highlight={myName}
      />

      <div className="mt-8 text-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="text-white/60 hover:text-white"
        >
          {t("common.home")}
        </Button>
      </div>
    </main>
  );
}
